import type { ReactNode } from "react";
import { clamp, cx } from "./util";

export interface MeterProps {
  /** 0–1. */
  value: number;
  /** Accessible name. Required — a bare bar tells a screen reader nothing. */
  label: string;
  /** Optional visible caption above the bar. */
  caption?: ReactNode;
  /** Spoken value, e.g. "62%". Falls back to the rounded percentage. */
  valueText?: string;
  className?: string;
  /** Progress bars that are purely decorative feedback (mic level) opt out
   *  of the progressbar role and are marked aria-hidden by the caller. */
  presentational?: boolean;
}

export function Meter({
  value,
  label,
  caption,
  valueText,
  className,
  presentational = false,
}: MeterProps) {
  const pct = clamp(value, 0, 1) * 100;

  return (
    <div className={cx("bd-meter-wrap", className)}>
      {caption ? <div className="t-micro bd-meter-caption">{caption}</div> : null}
      <div
        className="meter"
        role={presentational ? undefined : "progressbar"}
        aria-hidden={presentational || undefined}
        aria-label={presentational ? undefined : label}
        aria-valuemin={presentational ? undefined : 0}
        aria-valuemax={presentational ? undefined : 100}
        aria-valuenow={presentational ? undefined : Math.round(pct)}
        aria-valuetext={presentational ? undefined : (valueText ?? `${Math.round(pct)}%`)}
      >
        <div className="meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
